import {CSSProperties} from 'react';

import styled, {IStyledComponent, css, keyframes} from 'styled-components';

import {Button} from '@edna/components';
import {FormField, Label} from '@edna/components/FormField';
import {TFormFieldProps} from '@edna/components/FormField/primitives';
import {Row, Stack} from '@edna/components/primitives';
import {mixins as uiMixins} from '@edna/components/styles';
import {TSpacingSize} from '@edna/components/types';
import BaseArrowBackIcon from '@edna/icons/arrowBack.svg';

import {LoadingIcon as BaseLoadingIcon} from 'src/icons';

export {
  Text,
  SecondaryText,
  Hint,
  BigText,
  InlineLink,
  InlineRouterLink,
  SecondaryLink,
  Link,
  RouterLink,
  getFontSize,
  Error,
  NameLink,
} from './typography';

export const FormTitle = styled.div<{noMargin?: boolean}>`
  ${uiMixins.f5}

  ${({theme, noMargin}) => css`
    color: ${theme.branding.content.secondary};
    ${!noMargin &&
    css`
      margin-bottom: ${theme.spacing.s4};
    `}
  `};
`;

const ContentWithRightPreviewWrapper = styled.div`
  ${uiMixins.stack({size: '4', justify: 'space-between', direction: 'ROW'})};
`;

const ContentWithRightPreview = styled.div`
  width: 100%;
  max-width: ${({theme}) => theme.dimensions.formWidth}px;
`;

const LeftButton = styled(Button)`
  margin-right: ${({theme}) => theme.spacing.s2};
`;

const ModalLayout = styled.div`
  display: flex;
`;

const ModalLeftContent = styled.div`
  ${uiMixins.flexAlign('space-between', 'flex-start')}
  flex-direction: column;
  min-width: 325px;
  margin-right: ${({theme}) => theme.spacing.s8};
`;

const ModalLeftActions = styled.div`
  margin-top: auto;
  padding-top: ${({theme}) => theme.spacing.s6};
`;

const ModalImage = styled.svg`
  display: block;
  flex-shrink: 0;
  min-width: 274px;
  min-height: 198px;
`;

const ModalCenterImage = styled(ModalImage)`
  ${({theme}) => css`
    margin: ${theme.spacing.s6} auto 0;
  `}
`;

const NoShrinkImage = styled.svg<{$maxWidth?: string}>`
  flex-shrink: 0;
  max-width: ${({$maxWidth}) => $maxWidth};
`;

type TFieldFooterProps = {
  justifyContent?: CSSProperties['justifyContent'];
};

const FieldFooter = styled.div<TFieldFooterProps>`
  ${({theme, justifyContent = 'flex-start'}) => css`
    ${uiMixins.flexAlign(justifyContent, 'center')}
    margin-top: ${theme.spacing.s2};
  `}
`;

const NestedBlock = styled.div`
  margin: ${({theme: {spacing}}) => `${spacing.s3} 0 ${spacing.s1} ${spacing.s8}`};
`;

const Segments = styled.div`
  ${uiMixins.f1}
  display: inline-flex;
  width: max-content;
  ${({theme: {spacing, borderRadius, branding, palette, zIndex}}) => css`
    z-index: ${-zIndex.background};
    margin: -${spacing.s7} ${spacing.s3} ${spacing.s2} auto;
    padding: 2px ${spacing.s2};
    background-color: ${branding.content.secondary};
    color: ${palette.white};
    border-radius: ${borderRadius.br8};
  `}
`;

const FullForm = styled.form`
  width: 100%;
`;

type TCenterRowProps = {
  hasTopIndent?: boolean;
};

const CenterRow = styled(Row)<TCenterRowProps>`
  ${uiMixins.flexAlign('initial', 'center')}
  margin-top: ${({theme, hasTopIndent}) => (hasTopIndent ? theme.spacing.s4 : 0)};
`;

const ClickBlocker = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  cursor: not-allowed;

  ${({theme}) => css`
    z-index: ${theme.zIndex.notifications};
  `}
`;

const circular = keyframes`
  100% {
    transform: rotate(360deg)
  }
`;

const Loader = styled(BaseLoadingIcon)`
  ${uiMixins.wh('24px')};
  animation: ${circular} 3s linear infinite;
`;

const InlineLinkButton = styled.button<{weight?: number}>`
  ${uiMixins.pureButton}
  font-size: inherit;
  font-weight: ${({weight}) => weight ?? 'inherit'};
  color: currentColor;
  text-decoration: underline;
`;

const ButtonContent = styled(Stack).attrs((props) => ({
  ...props,
  size: '2',
  direction: 'ROW',
  align: 'center',
}))``;

const CommonLayout = styled.div`
  ${uiMixins.stack({direction: 'COLUMN'})};
  min-height: 100vh;
`;

const ArrowNextIcon = styled(BaseArrowBackIcon)`
  transform: rotate(180deg);
`;

type TFieldsRow = {
  size?: TSpacingSize;
  isWithoutLabels?: boolean;
  columnsWidth?: string;
  width?: string;
};

// TODO: https://jira.edna.ru/browse/EP-4330 add adaptability to FormField instead of crutches
const FieldsRow = styled.div<TFieldsRow>`
  display: grid;
  grid-auto-rows: auto;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;

  ${({theme, size = '0', isWithoutLabels, width = '100%'}) => css`
    grid-template-rows: repeat(${isWithoutLabels ? 1 : 2}, auto);
    grid-column-gap: ${theme.spacing[`s${size}`]};
    grid-row-gap: ${theme.spacing.s2};
    width: ${width};
  `};

  ${({columnsWidth}) =>
    !!columnsWidth &&
    css`
      grid-template-columns: ${columnsWidth};
    `};

  ${Label} {
    margin-top: auto;
  }

  ${FormField as IStyledComponent<'web', TFormFieldProps>} {
    margin: 0;
  }
`;

const Half = styled.div`
  width: 50%;
`;

const NoShrinkText = styled.span`
  white-space: nowrap;
`;

export {
  InlineLinkButton,
  Loader,
  ClickBlocker,
  CenterRow,
  FullForm,
  Segments,
  NestedBlock,
  FieldFooter,
  NoShrinkImage,
  ModalCenterImage,
  ModalImage,
  ModalLeftActions,
  ModalLeftContent,
  ModalLayout,
  LeftButton,
  ContentWithRightPreview,
  ContentWithRightPreviewWrapper,
  ButtonContent,
  CommonLayout,
  ArrowNextIcon,
  FieldsRow,
  Half,
  NoShrinkText,
};
